import pLimit from "p-limit";
import { API, graphqlOperation } from "aws-amplify";
import * as queries from "../../graphql/queries";
import * as mutations from "../../graphql/mutations";
import * as listIdQueries from "../../graphql/list-id-queries";
import * as adminQueries from "../../graphql/admin-queries";

import {
  // sortLinkedCars,
  isTrack,
  getLocationFullName,
  isValidTrackLength,
} from "../../utils/locationUtils";
import {
  getSortKeyForPosition,
  compareSortKeys,
} from "../../utils/timestampSortKey";
import {
  listTrackCarsForBackfill,
  listTrackCarsForMigration,
} from "../../graphql/trackCar/trackCar-queries";
import { updateTrackCarMini } from "../../graphql/trackCar/trackCar-mutations";
import { createInboundShipmentId } from "../../graphql/inbound/custom-mutations";
import { batchMutations, getEnv, logError } from "../../utils/appUtils";
import {
  AreaType,
  LocationType,
  ResourceType,
  UNSPOTTED_TRACK_ID,
} from "../../utils/constants";
import {
  InboundStatus,
  InboundState,
  LotType,
  CarrierType,
  InventoryStatus,
} from "../../utils/constants";

export const runScalabilityLoadTest = async (queryTerms) => {
  const { reloadYardId } = queryTerms;
  if (!reloadYardId || getEnv() === "prod") return;

  console.clear();
  console.log(`Starting Scalability Load Test for Yard: ${reloadYardId}`);

  try {
    const limit = pLimit(10);

    // 1. Get Track Area
    const areaResp = await API.graphql(
      graphqlOperation(queries.areaByActiveBySortKey, {
        reloadYardId,
        activeSortKey: { beginsWith: { active: "true" } },
        limit: 100,
      }),
    );
    const areas = areaResp?.data?.areaByActiveBySortKey?.items || [];
    const trackArea = areas.find((a) => a.areaType === AreaType.TRACK);
    if (!trackArea) throw new Error("No TRACK area found for this yard.");

    // 1.5 Get a valid Zone for the track area (Dynamically instead of hardcoded DEFAULT-T)
    const zoneResp = await API.graphql(
      graphqlOperation(queries.zoneByAreaBySortKey, {
        areaId: trackArea.id,
        limit: 1,
      }),
    );
    const trackZone = zoneResp?.data?.zoneByAreaBySortKey?.items?.[0];
    if (!trackZone)
      throw new Error("No zone found for the TRACK area in this yard.");

    // 2. Get an active Account
    const accountResp = await API.graphql(
      graphqlOperation(queries.accountByActiveByName, {
        reloadYardId,
        active: "true",
        limit: 1,
      }),
    );
    const account = accountResp?.data?.accountByActiveByName?.items?.[0];
    if (!account) throw new Error("No active account found for this yard.");

    // 3. Create 20 Tracks (Aisles) and their Locations
    console.log("Creating 20 tracks and locations...");
    const trackPromises = [];
    const trackIds = [];
    for (let i = 1; i <= 20; i++) {
      const aisleInput = {
        reloadYardId,
        active: "true",
        name: `TEST_SCAL_TRACK_${i}`,
        type: LocationType.UNSTRUCTURED,
        areaId: trackArea.id,
        zoneId: trackZone.id,
        bays: 1,
        levels: 1,
        positions: 1,
        totalLocations: 1,
        capacityUsed: 0,
        length: 3000, // 3000 feet supports ~50 cars
        sortKey: `999${i.toString().padStart(3, "0")}`,
        groups: account.groups,
      };
      trackPromises.push(
        limit(async () => {
          const aResp = await API.graphql(
            graphqlOperation(mutations.createAisle, { input: aisleInput }),
          );
          const aisle = aResp.data.createAisle;

          // Create a Location for this track to enable capacity tracking
          const locationInput = {
            reloadYardId,
            active: "true",
            name: aisle.name,
            type: LocationType.UNSTRUCTURED,
            areaId: trackArea.id,
            zoneId: aisle.zoneId,
            aisleId: aisle.id,
            bay: 1,
            level: 1,
            position: 1,
            sortKey: 1,
            productId: "", // Required for locationByProductBySortKey index
            length: 3000, // Matching aisle length for capacity calculation
            bulkCapacity: 50,
            locationLoad: {
              other: { selected: true, capacity: 50 }, // Total capacity 50 cars
              container: { selected: false },
              pallet: { selected: false },
              box: { selected: false },
              superSack: { selected: false },
              dryBulk: { selected: false },
              liquidBulk: { selected: false },
            },
            capacityUsed: 0,
            capacityAllocated: 0,
            groups: account.groups,
          };
          const lResp = await API.graphql(
            graphqlOperation(mutations.createLocation, {
              input: locationInput,
            }),
          );
          return lResp.data.createLocation.id;
        }),
      );
    }
    const trackResults = await Promise.all(trackPromises);
    trackResults.forEach((id) => trackIds.push(id));
    const trackSortState = new Map(trackIds.map((id) => [id, []]));

    // 4. Create 1000 Inbound Shipments and TrackCars
    console.log("Creating 1000 shipments and track cars...");
    const shipmentPromises = [];
    for (let i = 1; i <= 1000; i++) {
      const trackId = trackIds[(i - 1) % trackIds.length];
      const carNum = `TEST_SCAL_CAR_${i.toString().padStart(4, "0")}`;
      const trackSortList = trackSortState.get(trackId) || [];
      const sortKey = getSortKeyForPosition(
        trackSortList.length,
        trackSortList,
        true,
      );
      trackSortList.push({ sortKey });
      trackSortState.set(trackId, trackSortList);

      shipmentPromises.push(
        limit(async () => {
          // Create InboundShipment
          const shipmentInput = {
            reloadYardId,
            active: "true",
            status: InboundStatus.PLACED,
            state: InboundState.COMPLETED,
            poNumber: `PO_SCAL_${i}`,
            lotNumber: `LOT_SCAL_${i}`,
            lotType: LotType.CARLOT,
            railCarNumber: carNum,
            accountId: account.id,
            accountName: account.businessName,
            billToAccountId: account.defaultBillToAccountId || account.id,
            carrierType: CarrierType.RAIL,
            carrierName: "",
            shipFrom: account.businessName,
            createDateTime: new Date().toISOString(),
            releaseDateTime: new Date().toISOString(), // Required for inboundShipmentByAccountByStatusByDates index
            placedDateTime: new Date().toISOString(),
            scheduledDateTime: new Date().toISOString(),
            notifyDateTime: new Date().toISOString(),
            groups: account.groups,
          };
          const sResp = await API.graphql(
            graphqlOperation(createInboundShipmentId, {
              input: shipmentInput,
            }),
          );
          const shipmentId = sResp.data.createInboundShipment.id;

          // Create a dummy Inventory record for metadata (product name/color)
          const inventoryInput = {
            reloadYardId,
            accountId: account.id,
            accountName: account.businessName,
            inboundShipmentId: shipmentId,
            productName: "TEST SCALABILITY PRODUCT",
            productId: "TEST_SCAL_PROD",
            lotNumber: `LOT_SCAL_${i}`,
            lotType: LotType.CARLOT,
            status: InventoryStatus.ARRIVED,
            quantityOnHand: 1,
            quantityAdvised: 1,
            quantityPhysical: 1,
            quantityCommitted: 0,
            quantityReceived: 1,
            quantityMismatch: 0,
            weight: 0,
            pcs: 1,
            pcsHeight: 0,
            pcsWidth: 0,
            pcsLength: 0,
            pcsOnHand: 1,
            pcsCommitted: 0,
            active: "true",
            sortKey: "0",
            groups: account.groups,
          };
          await API.graphql(
            graphqlOperation(mutations.createInventorie, {
              input: inventoryInput,
            }),
          );

          // Create TrackCar
          const trackCarInput = {
            id: shipmentId,
            trackId,
            shipmentId,
            parentType: ResourceType.INBOUND,
            railCarNumber: carNum,
            accountId: account.id,
            reloadYardId,
            sortKey,
            // EOD fields: Initialize on CREATE only (cron updates for snapshots)
            eodTrackId: trackId,
            eodSortKey: sortKey,
            groups: account.groups,
          };
          await API.graphql(
            graphqlOperation(mutations.createTrackCar, {
              input: trackCarInput,
            }),
          );
        }),
      );
    }

    await Promise.all(shipmentPromises);
    console.log("Scalability Load Test Complete.");
  } catch (error) {
    logError("AdminDashboard:runScalabilityLoadTest", error, false);
  }
};

export const cleanupScalabilityLoadTest = async (queryTerms) => {
  const { reloadYardId } = queryTerms;
  if (!reloadYardId || getEnv() === "prod") return;

  console.clear();
  console.log(
    `Starting Comprehensive Cleanup for Scalability Load Test in Yard: ${reloadYardId}`,
  );

  try {
    const limit = pLimit(10);
    const logBatchFailures = (results, ids, label) => {
      const failedResults = results.filter((result) => !result.success);
      if (failedResults.length > 0) {
        const errorDetails = failedResults.map((result) => ({
          id: ids[result.index],
          error: result.error?.message || result.error,
        }));
        logError(
          `AdminDashboard:cleanupScalabilityLoadTest:${label}: Failed ${failedResults.length} of ${results.length}`,
          { failedResults: errorDetails },
          false,
        );
      }
      return failedResults.length;
    };

    // 1. Cleanup by Track (Aisle)
    console.log("Cleaning up tracks and cars on tracks...");

    const fetchAllTestAisles = async () => {
      const items = [];
      let nextToken = null;
      do {
        const response = await API.graphql(
          graphqlOperation(listIdQueries.listAisleIds, {
            filter: {
              reloadYardId: { eq: reloadYardId },
              name: { beginsWith: "TEST_SCAL_" },
            },
            limit: 100,
            nextToken,
          }),
        );
        const page = response?.data?.listAisles;
        if (page?.items?.length) {
          items.push(...page.items.filter(Boolean));
        }
        nextToken = page?.nextToken || null;
      } while (nextToken);
      return items;
    };

    const fetchAllTrackCarsForTrack = async (trackId) => {
      const items = [];
      let nextToken = null;
      do {
        const response = await API.graphql(
          graphqlOperation(listIdQueries.listTrackCarIds, {
            filter: { trackId: { eq: trackId } },
            limit: 500,
            nextToken,
          }),
        );
        const page = response?.data?.listTrackCars;
        if (page?.items?.length) {
          items.push(...page.items.filter(Boolean));
        }
        nextToken = page?.nextToken || null;
      } while (nextToken);
      return items;
    };

    const fetchAllLocationsForAisle = async (aisleId) => {
      const items = [];
      let nextToken = null;
      do {
        const response = await API.graphql(
          graphqlOperation(queries.listLocations, {
            filter: { aisleId: { eq: aisleId } },
            limit: 25,
            nextToken,
          }),
        );
        const page = response?.data?.listLocations;
        if (page?.items?.length) {
          items.push(...page.items.filter(Boolean));
        }
        nextToken = page?.nextToken || null;
      } while (nextToken);
      return items;
    };

    const fetchAllTestInventories = async () => {
      const items = [];
      let nextToken = null;
      do {
        const response = await API.graphql(
          graphqlOperation(queries.listInventories, {
            filter: {
              reloadYardId: { eq: reloadYardId },
              productName: { eq: "TEST SCALABILITY PRODUCT" },
            },
            limit: 50,
            nextToken,
          }),
        );
        const page = response?.data?.listInventories;
        if (page?.items?.length) {
          items.push(...page.items.filter(Boolean));
        }
        nextToken = page?.nextToken || null;
      } while (nextToken);
      return items;
    };

    let pass = 0;
    let aislesToDelete = [];
    do {
      aislesToDelete = await fetchAllTestAisles();
      if (!aislesToDelete.length) {
        break;
      }
      pass += 1;
      console.log(
        `Cleanup pass ${pass} – deleting ${aislesToDelete.length} test aisles...`,
      );
      const trackCarIdsToDelete = new Set();
      const locationIdsToDelete = new Set();
      const aisleIdsToDelete = new Set();

      await Promise.all(
        aislesToDelete
          .filter((aisle) => aisle?.id)
          .map((aisle) =>
            limit(async () => {
              try {
                const locations = await fetchAllLocationsForAisle(aisle.id);
                locations
                  .filter((loc) => loc?.id)
                  .forEach((loc) => locationIdsToDelete.add(loc.id));

                const legacyCars = await fetchAllTrackCarsForTrack(aisle.id);
                legacyCars.forEach(
                  (tc) => tc?.id && trackCarIdsToDelete.add(tc.id),
                );

                const locationCarLists = await Promise.all(
                  locations
                    .filter((loc) => loc?.id)
                    .map((loc) =>
                      limit(() => fetchAllTrackCarsForTrack(loc.id)),
                    ),
                );
                locationCarLists.forEach((locCars) => {
                  locCars.forEach(
                    (tc) => tc?.id && trackCarIdsToDelete.add(tc.id),
                  );
                });

                aisleIdsToDelete.add(aisle.id);
              } catch (aisleErr) {
                logError(
                  `AdminDashboard:cleanupScalabilityLoadTest:aisle:${aisle.id}`,
                  aisleErr,
                  false,
                );
              }
            }),
          ),
      );

      if (trackCarIdsToDelete.size > 0) {
        const trackCarIds = Array.from(trackCarIdsToDelete);
        const updateTrackCarOps = trackCarIds.map(
          (tcId) => () =>
            API.graphql(
              graphqlOperation(mutations.updateTrackCar, {
                input: { id: tcId, active: "false" },
              }),
            ),
        );
        const updateTrackCarResults = await batchMutations(updateTrackCarOps, {
          batchSize: 100,
          maxRetries: 3,
          retryDelay: 1000,
          batchDelay: 100,
        });
        logBatchFailures(
          updateTrackCarResults,
          trackCarIds,
          "updateTrackCar:inactive",
        );
      }

      if (locationIdsToDelete.size > 0) {
        const locationIds = Array.from(locationIdsToDelete);
        const deleteLocationOps = locationIds.map(
          (locationId) => () =>
            API.graphql(
              graphqlOperation(mutations.deleteLocation, {
                input: { id: locationId },
              }),
            ),
        );
        const deleteLocationResults = await batchMutations(deleteLocationOps, {
          batchSize: 100,
          maxRetries: 3,
          retryDelay: 1000,
          batchDelay: 100,
        });
        logBatchFailures(deleteLocationResults, locationIds, "deleteLocation");
      }

      let deletedThisPass = 0;
      if (aisleIdsToDelete.size > 0) {
        const aisleIds = Array.from(aisleIdsToDelete);
        const deleteAisleOps = aisleIds.map(
          (aisleId) => () =>
            API.graphql(
              graphqlOperation(mutations.deleteAisle, {
                input: { id: aisleId },
              }),
            ),
        );
        const deleteAisleResults = await batchMutations(deleteAisleOps, {
          batchSize: 100,
          maxRetries: 3,
          retryDelay: 1000,
          batchDelay: 100,
        });
        const failedAisleDeletes = logBatchFailures(
          deleteAisleResults,
          aisleIds,
          "deleteAisle",
        );
        deletedThisPass = deleteAisleResults.length - failedAisleDeletes;
      }

      if (deletedThisPass === 0) {
        console.warn(
          "CleanupScalabilityLoadTest: no aisles deleted in this pass; aborting to prevent infinite loop.",
        );
        break;
      }
    } while (aislesToDelete.length > 0);

    console.log("Cleaning up orphaned inventories...");
    const orphanInventories = await fetchAllTestInventories();
    if (orphanInventories.length > 0) {
      const inventoryIds = orphanInventories
        .filter((inv) => inv?.id)
        .map((inv) => inv.id);
      const deleteInventoryOps = inventoryIds.map(
        (inventoryId) => () =>
          API.graphql(
            graphqlOperation(mutations.deleteInventorie, {
              input: { id: inventoryId },
            }),
          ),
      );
      const deleteInventoryResults = await batchMutations(deleteInventoryOps, {
        batchSize: 100,
        maxRetries: 3,
        retryDelay: 1000,
        batchDelay: 100,
      });
      logBatchFailures(deleteInventoryResults, inventoryIds, "deleteInventory");
    }

    // 2. Cleanup orphaned Shipments and TrackCars (e.g. moved to other tracks or unspotted)
    console.log("Cleaning up shipments and orphaned track cars...");
    let nextToken = null;
    const shipmentsToDelete = [];
    do {
      const shipmentResp = await API.graphql(
        graphqlOperation(queries.searchInboundShipments, {
          filter: {
            reloadYardId: { eq: reloadYardId },
            railCarNumber: { wildcard: "TEST_SCAL_CAR_*" },
          },
          limit: 100,
          nextToken,
        }),
      );
      const shipments = shipmentResp?.data?.searchInboundShipments?.items || [];
      nextToken = shipmentResp?.data?.searchInboundShipments?.nextToken;
      shipmentsToDelete.push(...shipments.filter(Boolean));
    } while (nextToken);

    let totalDeletedShipments = 0;
    if (shipmentsToDelete.length > 0) {
      const shipmentIds = shipmentsToDelete
        .filter((shipment) => shipment?.id)
        .map((shipment) => shipment.id);

      const updateTrackCarOps = shipmentIds.map(
        (shipmentId) => () =>
          API.graphql(
            graphqlOperation(mutations.updateTrackCar, {
              input: { id: shipmentId, active: "false" },
            }),
          ),
      );
      const updateTrackCarResults = await batchMutations(updateTrackCarOps, {
        batchSize: 100,
        maxRetries: 3,
        retryDelay: 1000,
        batchDelay: 100,
      });
      const failedTrackCarUpdates = logBatchFailures(
        updateTrackCarResults,
        shipmentIds,
        "updateTrackCar:inactiveByShipment",
      );
      const successfulShipmentIds = shipmentIds.filter(
        (_, index) => updateTrackCarResults[index]?.success,
      );
      if (failedTrackCarUpdates > 0) {
        console.warn(
          `CleanupScalabilityLoadTest: Skipping ${failedTrackCarUpdates} shipments because TrackCar update failed.`,
        );
      }

      const deleteShipmentOps = successfulShipmentIds.map(
        (shipmentId) => async () => {
          const inventories = [];
          let inventoryNextToken = null;
          do {
            const invResp = await API.graphql(
              graphqlOperation(queries.listInventories, {
                filter: { inboundShipmentId: { eq: shipmentId } },
                limit: 100,
                nextToken: inventoryNextToken,
              }),
            );
            const page = invResp?.data?.listInventories;
            if (page?.items?.length) {
              inventories.push(...page.items.filter(Boolean));
            }
            inventoryNextToken = page?.nextToken || null;
          } while (inventoryNextToken);
          if (inventories.length > 0) {
            const inventoryIds = inventories
              .filter((inv) => inv?.id)
              .map((inv) => inv.id);
            const deleteInventoryOps = inventoryIds.map(
              (inventoryId) => () =>
                API.graphql(
                  graphqlOperation(mutations.deleteInventorie, {
                    input: { id: inventoryId },
                  }),
                ),
            );
            const deleteInventoryResults = await batchMutations(
              deleteInventoryOps,
              {
                batchSize: 50,
                maxRetries: 3,
                retryDelay: 1000,
                batchDelay: 100,
              },
            );
            const failedInventoryDeletes = deleteInventoryResults.filter(
              (result) => !result.success,
            );
            if (failedInventoryDeletes.length > 0) {
              logError(
                `AdminDashboard:cleanupScalabilityLoadTest:deleteShipmentInventories:${shipmentId}`,
                { failedInventoryDeletes },
                false,
              );
              throw new Error(
                `Inventory deletions failed for shipment ${shipmentId}`,
              );
            }
          }

          return API.graphql(
            graphqlOperation(mutations.deleteInboundShipment, {
              input: { id: shipmentId },
            }),
          );
        },
      );

      const deleteShipmentResults = await batchMutations(deleteShipmentOps, {
        batchSize: 50,
        maxRetries: 3,
        retryDelay: 1000,
        batchDelay: 100,
      });
      const failedShipmentDeletes = logBatchFailures(
        deleteShipmentResults,
        successfulShipmentIds,
        "deleteInboundShipment",
      );
      totalDeletedShipments =
        deleteShipmentResults.length - failedShipmentDeletes;
      console.log(`Deleted ${totalDeletedShipments} shipments...`);
    }

    console.log(
      `Scalability Cleanup Complete: Tracks and ${totalDeletedShipments} shipments removed.`,
    );
  } catch (error) {
    logError("AdminDashboard:cleanupScalabilityLoadTest", error, false);
  }
};

/**
 * Run batched length=1 updates with limiter; returns count of fulfilled, logs rejections.
 * @param {Array<{ id: string }>} toUpdate
 * @param {import('p-limit').LimitFunction} limiter
 * @param {(item: { id: string }) => Promise<unknown>} updateOne
 * @param {string} logLabel
 * @returns {Promise<number>}
 */
const runLengthUpdates = async (toUpdate, limiter, updateOne, logLabel) => {
  const results = await Promise.allSettled(
    toUpdate.map((item) => limiter(() => updateOne(item))),
  );
  results
    .filter((r) => r.status === "rejected")
    .forEach((r) => logError(logLabel, r.reason, false));
  return results.filter((r) => r.status === "fulfilled").length;
};

/**
 * Extract items and nextToken from a GraphQL list response.
 * @param {import('aws-amplify').GraphQLResult} resp - Raw GraphQL response (data key is used).
 * @param {string} dataKey - Key under resp.data for the list (e.g. "areaByActiveBySortKey").
 * @returns {{ items: Array<{ id: string, [key: string]: unknown }>, nextToken: string | null }}
 */
const getPage = (resp, dataKey) => ({
  items: resp?.data?.[dataKey]?.items ?? [],
  nextToken: resp?.data?.[dataKey]?.nextToken ?? null,
});

/**
 * Filter items needing length backfill (see isValidTrackLength) and run batched updates.
 * @param {Array<{ id?: string, length?: unknown }>} items - Page of entities (aisles or locations).
 * @param {import('p-limit').LimitFunction} limiter
 * @param {(item: { id: string }) => Promise<unknown>} updateOne
 * @param {string} logLabel
 * @returns {Promise<number>} Count of updates that fulfilled.
 */
const backfillLengthBatch = (items, limiter, updateOne, logLabel) =>
  runLengthUpdates(
    items.filter((item) => item?.id && !isValidTrackLength(item.length)),
    limiter,
    updateOne,
    logLabel,
  );

/**
 * Backfill one yard: TRACK-area Aisle/Location length = 1 when length is not present or < 1 (see isValidTrackLength).
 * @param {string} reloadYardId
 * @param {import('p-limit').LimitFunction} limiter
 * @param {number} pageLimit
 * @returns {{ aislesScanned: number, aislesUpdated: number, locationsScanned: number, locationsUpdated: number, skipped: boolean }}
 */
const backfillLocationLengthForYard = async (
  reloadYardId,
  limiter,
  pageLimit,
) => {
  let aislesScanned = 0;
  let aislesUpdated = 0;
  let locationsScanned = 0;
  let locationsUpdated = 0;

  const activeAreas = [];
  let areaNextToken = null;
  do {
    const areaResp = await API.graphql(
      graphqlOperation(queries.areaByActiveBySortKey, {
        reloadYardId,
        activeSortKey: { beginsWith: { active: "true" } },
        limit: 100,
        nextToken: areaNextToken,
      }),
    );
    const { items: areas, nextToken: nextArea } = getPage(
      areaResp,
      "areaByActiveBySortKey",
    );
    areaNextToken = nextArea ?? null;
    activeAreas.push(...areas);
  } while (areaNextToken);
  if (activeAreas.length === 0) {
    return {
      aislesScanned: 0,
      aislesUpdated: 0,
      locationsScanned: 0,
      locationsUpdated: 0,
      skipped: true,
    };
  }

  for (const area of activeAreas) {
    const isTrackArea = area?.areaType === AreaType.TRACK;
    if (!isTrackArea) continue;
    let aisleNextToken = null;
    do {
      const aisleResp = await API.graphql(
        graphqlOperation(queries.aisleByActiveByAreaBySortKey, {
          areaId: area.id,
          activeSortKey: { beginsWith: { active: "true" } },
          limit: pageLimit,
          nextToken: aisleNextToken,
        }),
      );
      const { items: aisleItems, nextToken: nextAisle } = getPage(
        aisleResp,
        "aisleByActiveByAreaBySortKey",
      );
      aisleNextToken = nextAisle ?? null;
      aislesScanned += aisleItems.length;
      aislesUpdated += await backfillLengthBatch(
        aisleItems,
        limiter,
        (a) =>
          API.graphql(
            graphqlOperation(mutations.updateAisle, {
              input: { id: a.id, length: 1 },
            }),
          ),
        "backfillLocationLength:updateAisle",
      );
    } while (aisleNextToken);

    let locNextToken = null;
    do {
      const locResp = await API.graphql(
        graphqlOperation(queries.locationByAreaBySortKey, {
          reloadYardId,
          areaIdSortKey: { beginsWith: { areaId: area.id } },
          limit: pageLimit,
          nextToken: locNextToken,
        }),
      );
      const { items, nextToken: nextLoc } = getPage(
        locResp,
        "locationByAreaBySortKey",
      );
      locNextToken = nextLoc ?? null;
      locationsScanned += items.length;
      locationsUpdated += await backfillLengthBatch(
        items,
        limiter,
        (loc) =>
          API.graphql(
            graphqlOperation(mutations.updateLocation, {
              input: { id: loc.id, length: 1 },
            }),
          ),
        "backfillLocationLength:updateLocation",
      );
    } while (locNextToken);
  }

  return {
    aislesScanned,
    aislesUpdated,
    locationsScanned,
    locationsUpdated,
    skipped: false,
  };
};

/**
 * Backfill TRACK-area Aisle/Location length = 1 in all yards.
 * "Needs backfill" = null, undefined, empty string, 0, or any value < 1.
 *
 * @param {{ reloadYardId?: string }} queryTerms - from dialog (unused; always runs all active yards)
 * @param {(msg: string, severity?: string) => void} [notifyCallback] - optional notify
 */
export const backfillLocationLength = async (queryTerms, notifyCallback) => {
  console.clear();
  console.log(
    "Starting backfillLocationLength (TRACK-area aisles/locations, all yards)…",
  );

  const limiter = pLimit(10);
  const pageLimit = 200;
  let totalAislesScanned = 0;
  let totalAislesUpdated = 0;
  let totalLocationsScanned = 0;
  let totalLocationsUpdated = 0;
  let yardsProcessed = 0;
  let yardsSkipped = 0;
  let yardsFailed = 0;

  try {
    const yardIds = [];
    let yardNextToken = null;
    do {
      const yardResp = await API.graphql(
        graphqlOperation(listIdQueries.listReloadYardIds, {
          filter: { active: { eq: "true" } },
          limit: 100,
          nextToken: yardNextToken,
        }),
      );
      const { items: yardItems, nextToken: nextYard } = getPage(
        yardResp,
        "listReloadYards",
      );
      yardNextToken = nextYard ?? null;
      yardItems.forEach((yard) => {
        if (yard?.id) yardIds.push(yard.id);
      });
    } while (yardNextToken);

    if (yardIds.length === 0) {
      if (notifyCallback) {
        notifyCallback("No active reload yards found.", "warning");
      }
      return;
    }

    for (const reloadYardId of yardIds) {
      try {
        const result = await backfillLocationLengthForYard(
          reloadYardId,
          limiter,
          pageLimit,
        );
        totalAislesScanned += result.aislesScanned;
        totalAislesUpdated += result.aislesUpdated;
        totalLocationsScanned += result.locationsScanned;
        totalLocationsUpdated += result.locationsUpdated;
        if (result.skipped) yardsSkipped += 1;
        else yardsProcessed += 1;
      } catch (err) {
        logError(
          `AdminDashboard:backfillLocationLength:yard:${reloadYardId}`,
          err,
          false,
        );
        yardsFailed += 1;
      }
    }

    const yardSummary = `yards: ${yardsProcessed} processed, ${yardsSkipped} skipped (no active areas)${yardsFailed > 0 ? `, ${yardsFailed} failed` : ""}`;
    console.log(
      `backfillLocationLength done. ${yardSummary}. aisles: scanned=${totalAislesScanned} updated=${totalAislesUpdated}; locations: scanned=${totalLocationsScanned} updated=${totalLocationsUpdated}`,
    );
    if (notifyCallback) {
      const msg = `Location length backfill (${yardIds.length} yard(s)): ${totalAislesUpdated} aisles and ${totalLocationsUpdated} locations set to length=1.`;
      notifyCallback(
        yardsFailed > 0
          ? `${msg} ${yardsFailed} yard(s) failed (see console).`
          : msg,
      );
    }
  } catch (err) {
    logError(
      "AdminDashboard:backfillLocationLength",
      err,
      notifyCallback ?? false,
    );
    if (notifyCallback) {
      notifyCallback("backfillLocationLength failed. Check console.", "error");
    }
  }
};

export const backfillTrackCarAisleZoneArea = async (notifyCallback) => {
  console.clear();
  console.log("Starting TrackCar aisleId/zoneId/areaId backfill…");

  const limit = 500;
  const limiter = pLimit(15);
  let scanned = 0;
  let updated = 0;
  let skipped = 0;
  let failed = 0;

  try {
    const trackCars = [];
    let nextToken = null;
    do {
      const response = await API.graphql(
        graphqlOperation(listTrackCarsForBackfill, {
          limit,
          nextToken,
        }),
      );
      const page = response?.data?.listTrackCars;
      const items = page?.items?.filter(Boolean) || [];
      trackCars.push(...items);
      nextToken = page?.nextToken || null;
    } while (nextToken);

    console.log(
      `Backfilling aisleId/zoneId/areaId for ${trackCars.length} TrackCar(s) across all yards.`,
    );
    if (trackCars.length === 0) {
      notifyCallback?.("No TrackCars found. Nothing to backfill.", "info");
      return;
    }

    const tasks = trackCars.map((trackCar) =>
      limiter(async () => {
        if (!trackCar?.id || !trackCar?.trackId) {
          return "skipped";
        }
        if (trackCar.trackId === UNSPOTTED_TRACK_ID) {
          return "skipped";
        }
        try {
          const locResponse = await API.graphql(
            graphqlOperation(queries.getLocation, { id: trackCar.trackId }),
          );
          const location = locResponse?.data?.getLocation;
          if (!location?.areaId || !location?.zoneId || !location?.aisleId) {
            return "skipped";
          }
          if (
            trackCar.areaId === location.areaId &&
            trackCar.zoneId === location.zoneId &&
            trackCar.aisleId === location.aisleId
          ) {
            return "skipped";
          }

          await API.graphql(
            graphqlOperation(updateTrackCarMini, {
              input: {
                id: trackCar.id,
                areaId: location.areaId,
                zoneId: location.zoneId,
                aisleId: location.aisleId,
              },
            }),
          );
          return "updated";
        } catch (err) {
          console.warn(
            `backfillTrackCarAisleZoneArea: trackCar ${trackCar.id} trackId ${trackCar.trackId}:`,
            err,
          );
          return "failed";
        }
      }),
    );

    const results = await Promise.all(tasks);
    scanned = results.length;
    updated = results.filter((r) => r === "updated").length;
    skipped = results.filter((r) => r === "skipped").length;
    failed = results.filter((r) => r === "failed").length;

    console.log(
      `TrackCar aisleId/zoneId/areaId backfill complete. scanned=${scanned}, updated=${updated}, skipped=${skipped}, failed=${failed}`,
    );
    notifyCallback?.(
      `Backfill complete: ${updated} updated, ${skipped} skipped, ${failed} failed (${scanned} total).`,
      "info",
    );
  } catch (err) {
    logError(
      "AdminDashboard:backfillTrackCarAisleZoneArea",
      err,
      notifyCallback,
    );
    if (notifyCallback) {
      notifyCallback(
        "backfillTrackCarAisleZoneArea failed. Check console.",
        "error",
      );
    }
  }
};

export const migrateUnspottedTrackCarsActiveState = async (notifyCallback) => {
  console.clear();
  console.log("Starting unspotted TrackCar active-state migration...");

  const limit = 500;
  const LEGACY_UNSPOTTED_TRACK_ID = "UNPLACED_VIRTUAL";
  const fetchTrackCars = async (query, filter) => {
    const items = [];
    let nextToken = null;
    do {
      const response = await API.graphql(
        graphqlOperation(query, {
          limit,
          nextToken,
          filter,
        }),
      );
      const page = response?.data?.listTrackCars;
      const pageItems = page?.items?.filter(Boolean) || [];
      items.push(...pageItems);
      nextToken = page?.nextToken || null;
    } while (nextToken);
    return items;
  };
  const isShipmentCollected = (trackCar) => {
    if (trackCar?.parentType === ResourceType.INBOUND) {
      return trackCar?.inbound?.isCollected === true;
    }
    if (trackCar?.parentType === ResourceType.OUTBOUND) {
      return trackCar?.outbound?.isCollected === true;
    }
    return false;
  };

  try {
    const yardIds = [];
    let yardNextToken = null;
    do {
      const yardResp = await API.graphql(
        graphqlOperation(listIdQueries.listReloadYardIds, {
          filter: { active: { eq: "true" } },
          limit: 100,
          nextToken: yardNextToken,
        }),
      );
      const { items: yardItems, nextToken: nextYard } = getPage(
        yardResp,
        "listReloadYards",
      );
      yardItems.forEach((yard) => {
        if (yard?.id) yardIds.push(yard.id);
      });
      yardNextToken = nextYard ?? null;
    } while (yardNextToken);

    if (yardIds.length === 0) {
      notifyCallback?.(
        "No active reload yards found for migration.",
        "warning",
      );
      return;
    }

    const migrationStats = {
      yardsTotal: yardIds.length,
      yardsProcessed: 0,
      yardsFailed: 0,
      scanned: 0,
      scannedUnspotted: 0,
      scannedCollectedTrackCleanup: 0,
      migrated: 0,
      migratedUnspotted: 0,
      migratedCollectedTrackCleanup: 0,
      failed: 0,
      failedUnspotted: 0,
      failedCollectedTrackCleanup: 0,
    };

    for (const reloadYardId of yardIds) {
      try {
        const toMigrateById = new Map();
        const trackCars = await fetchTrackCars(listTrackCarsForMigration, {
          and: [
            {
              reloadYardId: { eq: reloadYardId },
            },
            {
              trackId: { exists: true },
            },
          ],
        });

        // Priority order:
        // 1) Unspotted migration (legacy/new virtual IDs) -> active=true, trackId=UNSPOTTED
        // 2) Collected cleanup (inactive TrackCar or shipment.isCollected=true with real trackId)
        trackCars.forEach((trackCar) => {
          if (!trackCar?.id) return;

          const trackId = trackCar.trackId || "";
          const isVirtualUnspottedTrackId =
            trackId === LEGACY_UNSPOTTED_TRACK_ID ||
            trackId === UNSPOTTED_TRACK_ID;

          if (isVirtualUnspottedTrackId) {
            toMigrateById.set(trackCar.id, {
              category: "unspotted",
              trackCar,
            });
            return;
          }

          const isCollectedTrackCar = trackCar.active === "false";
          const isCollectedShipment = isShipmentCollected(trackCar);
          if (trackId && (isCollectedTrackCar || isCollectedShipment)) {
            toMigrateById.set(trackCar.id, {
              category: "collectedTrackCleanup",
              trackCar,
            });
          }
        });

        const toMigrate = Array.from(toMigrateById.values());
        migrationStats.scanned += toMigrate.length;
        migrationStats.scannedUnspotted += toMigrate.filter(
          (item) => item.category === "unspotted",
        ).length;
        migrationStats.scannedCollectedTrackCleanup += toMigrate.filter(
          (item) => item.category === "collectedTrackCleanup",
        ).length;

        if (toMigrate.length === 0) {
          migrationStats.yardsProcessed += 1;
          continue;
        }

        const migrationOps = toMigrate.map(({ category, trackCar }) => ({
          category,
          run: () => {
            const currentSortKey = trackCar.sortKey || "";
            const currentEodTrackId =
              trackCar.eodTrackId || trackCar.trackId || "";
            const currentEodSortKey =
              trackCar.eodSortKey || trackCar.sortKey || "";

            if (category === "unspotted") {
              const input = {
                id: trackCar.id,
                active: "true",
                trackId: UNSPOTTED_TRACK_ID,
                sortKey: currentSortKey,
                eodTrackId: UNSPOTTED_TRACK_ID,
                eodSortKey: currentSortKey,
              };
              return API.graphql(
                graphqlOperation(mutations.updateTrackCar, {
                  input,
                }),
              );
            }

            return API.graphql(
              graphqlOperation(mutations.updateTrackCar, {
                input: {
                  id: trackCar.id,
                  active: "false",
                  trackId: "",
                  sortKey: "",
                  eodTrackId: currentEodTrackId,
                  eodSortKey: currentEodSortKey,
                },
              }),
            );
          },
        }));

        const results = await batchMutations(
          migrationOps.map((op) => op.run),
          {
            batchSize: 100,
            maxRetries: 3,
            retryDelay: 1000,
            batchDelay: 100,
          },
        );

        const failed = [];
        results.forEach((result, index) => {
          const opCategory = migrationOps[index]?.category;
          if (result?.success) {
            migrationStats.migrated += 1;
            if (opCategory === "unspotted") {
              migrationStats.migratedUnspotted += 1;
            } else if (opCategory === "collectedTrackCleanup") {
              migrationStats.migratedCollectedTrackCleanup += 1;
            }
          } else {
            failed.push(result);
            migrationStats.failed += 1;
            if (opCategory === "unspotted") {
              migrationStats.failedUnspotted += 1;
            } else if (opCategory === "collectedTrackCleanup") {
              migrationStats.failedCollectedTrackCleanup += 1;
            }
          }
        });
        migrationStats.yardsProcessed += 1;

        if (failed.length > 0) {
          logError(
            "AdminDashboard:migrateUnspottedTrackCarsActiveState:partialFailure",
            {
              reloadYardId,
              failedCount: failed.length,
              failed: failed.map((f) => f.error?.message || f.error),
            },
            false,
          );
        }
      } catch (yardErr) {
        migrationStats.yardsFailed += 1;
        logError(
          "AdminDashboard:migrateUnspottedTrackCarsActiveState:yardFailure",
          { reloadYardId, error: yardErr },
          false,
        );
      }
    }

    const summary = `TrackCar migration complete: yards=${migrationStats.yardsProcessed}/${migrationStats.yardsTotal} processed, yardFailures=${migrationStats.yardsFailed}, scanned=${migrationStats.scanned} (unspotted=${migrationStats.scannedUnspotted}, collectedCleanup=${migrationStats.scannedCollectedTrackCleanup}), migrated=${migrationStats.migrated} (unspotted=${migrationStats.migratedUnspotted}, collectedCleanup=${migrationStats.migratedCollectedTrackCleanup}), failed=${migrationStats.failed} (unspotted=${migrationStats.failedUnspotted}, collectedCleanup=${migrationStats.failedCollectedTrackCleanup}).`;
    console.log(summary);
    notifyCallback?.(
      summary,
      migrationStats.failed > 0 || migrationStats.yardsFailed > 0
        ? "warning"
        : "info",
    );
  } catch (err) {
    logError("AdminDashboard:migrateUnspottedTrackCarsActiveState", err, false);
    notifyCallback?.(
      "Unspotted TrackCar migration failed before completion. Check console.",
      "error",
    );
  }
};
